import React, { useEffect, useState, useRef } from 'react';
import { Menu, MenuItem, TextField, InputAdornment } from '@mui/material';
import { TableColumnFilterProps, SelectionMode } from '@models/table.model';
import { FilterIcon, CloseIcon } from '@components/icons/svg-icon';
import { useTranslation } from 'react-i18next';
import checkIcon from '@assets/icons/check.svg';

const TableColumnFilter: React.FC<TableColumnFilterProps> = ({
  columnId,
  selectionMode = SelectionMode.Single,
  onFilterChange,
  value,
  filterData,
  clearFilters,
  renderFilterItem,
  showSearchInput = true
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();

  const [selectedOptions, setSelectedOptions] = useState<string[]>(value);
  const [searchQuery, setSearchQuery] = useState('');
  const tempSelectedOptions = useRef<string[]>(value);
  const prevSelectedOptions = useRef<string[]>(value);

  useEffect(() => {
    if (selectionMode === SelectionMode.Multiple) {
      tempSelectedOptions.current = [...selectedOptions];
    }
  }, [open]);

  useEffect(() => {
    setSelectedOptions(value);
    tempSelectedOptions.current = [...value];
    prevSelectedOptions.current = [...value];
  }, [value]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const optionValue = event.target.value;
    const checked = event.target.checked;

    if (selectionMode === SelectionMode.Multiple) {
      if (checked) {
        if (!tempSelectedOptions.current.includes(optionValue)) {
          tempSelectedOptions.current.push(optionValue);
        }
      } else {
        tempSelectedOptions.current = tempSelectedOptions.current.filter(
          (val) => val !== optionValue
        );
      }
      setSelectedOptions([...tempSelectedOptions.current]);
    } else if (selectionMode === SelectionMode.Single) {
      const alreadySelected = selectedOptions.includes(optionValue);
      if (alreadySelected) {
        setSelectedOptions([]);
        onFilterChange?.(columnId, []);
      } else {
        setSelectedOptions([optionValue]);
        onFilterChange?.(columnId, [optionValue]);
      }
      setAnchorEl(null);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setSearchQuery('');
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (
      selectionMode === SelectionMode.Multiple &&
      JSON.stringify(prevSelectedOptions.current) !==
        JSON.stringify(tempSelectedOptions.current)
    ) {
      prevSelectedOptions.current = [...tempSelectedOptions.current];
      onFilterChange?.(columnId, tempSelectedOptions.current);
    }
  };

  const handleSearchClear = () => {
    setSearchQuery('');
  };

  const handleClearFilters = () => {
    clearFilters?.(columnId);
  };

  const filteredData = filterData?.filter((option: any) =>
    option.title?.toLowerCase().includes(searchQuery?.toLowerCase())
  );

  return (
    <>
      <div className="cursor-pointer" onClick={handleClick}>
        <FilterIcon className="!w-[18px] !h-[18px] filter-icon-hover max-lg-xl:!w-[16px] max-lg-xl:!h-[16px]" />
      </div>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableAutoFocusItem
        disableEnforceFocus
        disableRestoreFocus
        slotProps={{
          list: {
            disableListWrap: true,
            autoFocusItem: false,
            onMouseDown: (e: any) => e.stopPropagation(),
            onKeyDown: (e: any) => e.stopPropagation()
          }
        }}
      >
        {showSearchInput && (
          <div
            className="px-2 mb-3"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <TextField
              sx={{ width: '100%' }}
              size="small"
              placeholder="Search..."
              fullWidth
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              slotProps={{
                input: {
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <button
                        onClick={handleSearchClear}
                        className="cursor-pointer"
                      >
                        <CloseIcon
                          stroke="var(--primary)"
                          className="!w-[14px] !h-[14px]"
                        />
                      </button>
                    </InputAdornment>
                  ),
                  sx: {
                    '& .MuiInputBase-input': {
                      padding: '10px 12px !important'
                    }
                  }
                }
              }}
            />
          </div>
        )}

        <div
          className="!min-w-[286px] max-h-[310px] overflow-y-auto max-lg-xl:!min-w-[200px]"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {filteredData?.length === 0 ? (
            <MenuItem disabled className="!opacity-50 !pointer-events-none">
              No options
            </MenuItem>
          ) : (
            filteredData?.map((option: any) => {
              const isChecked =
                selectionMode === SelectionMode.Multiple
                  ? tempSelectedOptions.current.includes(option.value)
                  : selectedOptions.includes(option.value);

              return (
                <MenuItem
                  key={option.title}
                  className="!flex !items-center"
                  onClick={() => {
                    const checked = !tempSelectedOptions.current.includes(
                      option.value
                    );
                    handleCheckboxChange({
                      target: {
                        checked,
                        value: option.value
                      }
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                >
                  {renderFilterItem ? (
                    renderFilterItem({
                      option,
                      isSelected: isChecked,
                      onChange: (e) => handleCheckboxChange(e)
                    })
                  ) : (
                    <div className="flex items-center w-full relative">
                      {option.title}
                      {isChecked && (
                        <span
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 max-[1715px]:w-3.25 max-[1715px]:h-3.25"
                          style={{
                            maskImage: `url("${checkIcon}")`,
                            WebkitMaskImage: `url("${checkIcon}")`,
                            WebkitMaskRepeat: 'no-repeat',
                            maskRepeat: 'no-repeat',
                            WebkitMaskSize: 'contain',
                            maskSize: 'contain',
                            backgroundColor: 'var(--primary)'
                          }}
                        />
                      )}
                    </div>
                  )}
                </MenuItem>
              );
            })
          )}
        </div>

        <div className="flex items-center pt-2 px-5 border-t border-(--gray-8) max-lg-xl:px-4">
          {(selectedOptions.length > 0 || value.length > 0) && (
            <button
              onClick={handleClearFilters}
              className="cursor-pointer text-[14px] font-medium text-(--primary) underline"
            >
              {t('reset_filters')}
            </button>
          )}
        </div>
      </Menu>
    </>
  );
};

export default TableColumnFilter;
